import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRevenueHistory } from '../../../stores/appRevenueHistoryStore';
import { useAppUserDefaultStore } from '../../../stores/appUserDefaultStore';
import { useTimeIntervalStore } from '../../../stores/appTimeIntervalStore';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, DollarSign } from 'lucide-react';
import { RevenueHistoryData, RevenueItem } from '../../revenuehistory/types';
import { startOfPeriod, toDate, withinPeriod, eur } from '../../revenuehistory/utils';
import { singleItemTimelineAgg } from '../../revenuehistory/aggregations';
import { ChartPlaceholder } from '../../revenuehistory/ChartPlaceholder';
import HeaderControls from '../../revenuehistory/sections/HeaderControls';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Rectangle } from 'recharts';
import AppFooter from '../../common/AppFooter';
import { useAppSettingsStore } from '../../../stores/appSettingsStore';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';


// Custom rounded bar shape component
const RoundedBar = (props: any) => {
  const { fill } = props;
  return (
    <Rectangle
      {...props}
      radius={[10, 10, 0, 0]} // Top-left, top-right, bottom-right, bottom-left
      fill={fill}
    />
  );
};

const RevenueHistoryItemDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { itemName } = useParams<{ itemName: string }>();
  const darkMode = useAppSettingsStore(s => s.darkMode);
  
  // Zustand stores
  const revenueHistoryStore = useRevenueHistory();
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const showExVat = useAppUserDefaultStore(s => s.showExVat);
  const restaurantId = selectedRestaurant?.id;
  const view = useTimeIntervalStore(s => s.view);
  const cursor = useTimeIntervalStore(s => s.cursor);
  const setView = useTimeIntervalStore(s => s.setView);
  const setCursor = useTimeIntervalStore(s => s.setCursor);
  
  // Revenue source store
  const loadRevenueSourceData = useRestaurantStore(s => s.loadAllData);
  const revenueSourcesMap = useRestaurantStore(s => s.restaurantRevenueSources);

  // Load data only if not already loaded
  useEffect(() => {
    loadRevenueSourceData();
  }, [loadRevenueSourceData]);

  useEffect(() => {
    if (restaurantId && !revenueHistoryStore.isDataLoadedFor(restaurantId)) {
      revenueHistoryStore.load(restaurantId, revenueSourcesMap);
    }
  }, [restaurantId, revenueHistoryStore, revenueSourcesMap]);

  // Use store data directly
  const revenueData: RevenueHistoryData[] = revenueHistoryStore.revenueHistoryList;
  
  // Filter data for the specific item
  const itemData = revenueData.filter(r => {
    return r.revenueData.categories.some(category => 
      category.items.some(item => item.name === itemName)
    );
  });

  // Calculate aggregations with filters
  const start = startOfPeriod(cursor, view);
  
  // Calculate KPIs for the specific item in the selected period
  const calculateItemKpis = () => {
    let totalQuantity = 0;
    let totalRevenue = 0;
    let latestPrice = 0;
    
    const filteredData = itemData.filter(r => withinPeriod(toDate(r.date), start, view));
    
    filteredData.forEach(r => {
      r.revenueData.categories.forEach(category => {
        category.items
          .filter(item => item.name === itemName)
          .forEach(item => {
            totalQuantity += (item.quantity || 0);
            totalRevenue += showExVat ? (item.exVat || 0) : (item.price || 0);
            
            // Get the latest price (most recent entry)
            const itemPrice = item.quantity && item.quantity > 0 
              ? (showExVat ? (item.exVat || 0) : (item.price || 0)) / item.quantity 
              : 0;
            if (itemPrice > 0) {
              latestPrice = itemPrice;
            }
          });
      });
    });
    
    return { totalQuantity, totalRevenue, latestPrice };
  };

  const { totalQuantity, totalRevenue, latestPrice } = calculateItemKpis();

  const quantityTimeline = singleItemTimelineAgg(
    itemData,
    itemName!,
    start,
    view,
    (item: RevenueItem) => item.quantity || 0
  ).map(({ date, value, isSelected }) => ({
    date,
    quantity: value,
    isSelected
  }));

  const revenueTimeline = singleItemTimelineAgg(
    itemData,
    itemName!,
    start,
    view,
    (item: RevenueItem) => (showExVat ? item.exVat || 0 : item.price || 0)
  ).map(({ date, value, isSelected }) => ({
    date,
    revenue: value,
    isSelected
  }));

  // Format chart labels
  const formatXAxisLabel = (tickItem: string) => {
    const date = new Date(tickItem);
    if (isNaN(date.getTime())) return tickItem;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };
  
  if (!itemName) {
    return (
      <div className="p-4 max-w-7xl mx-auto">
        <div className="text-center text-muted dark:text-muted-dark">
          {t('revenueHistory.itemNotFound')}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <MobilePageHeader title={itemName} />
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => navigate('/revenuehistory/items')}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-text dark:text-text-dark bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-lg hover:bg-muted/10 dark:hover:bg-muted-dark/10 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('revenueHistory.backToItems')}
          </button>
          <div className="hidden lg:flex items-center gap-3">
            <DollarSign className="w-8 h-8 text-green-600 dark:text-green-400" />
            <h1 className="text-3xl font-bold text-text dark:text-text-dark">
              {itemName}
            </h1>
          </div>
        </div>
        
        {/* Time Controls */}
        <div className="mb-4">
          <HeaderControls 
            view={view} 
            setView={setView} 
            cursor={cursor} 
            setCursor={setCursor}
            revenueHistory={revenueData}
          />
        </div>
      </div>

      {/* KPIs Section */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
      <div className="bg-card dark:bg-card-dark rounded-lg shadow-md p-4">
          <div className="text-sm text-muted dark:text-muted-dark">{t('revenueHistory.totalRevenueKpi')}</div>
          <div className="text-lg font-semibold text-text dark:text-text-dark">{eur(totalRevenue)}</div>
        </div>
        <div className="bg-card dark:bg-card-dark rounded-lg shadow-md p-4">
          <div className="text-sm text-muted dark:text-muted-dark">{t('revenueHistory.totalQuantity')}</div>
          <div className="text-lg font-semibold text-text dark:text-text-dark">{totalQuantity}</div>
        </div>
        <div className="bg-card dark:bg-card-dark rounded-lg shadow-md p-4">
          <div className="text-sm text-muted dark:text-muted-dark">{t('revenueHistory.latestPrice')}</div>
          <div className="text-lg font-semibold text-text dark:text-text-dark">{eur(latestPrice)}</div>
        </div>
      </div>
      
      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Quantity Over Time Chart */}
        <div className="bg-card dark:bg-card-dark rounded-lg shadow-md p-4">
          <h3 className="font-semibold mb-2 text-text dark:text-text-dark">{t('revenueHistory.quantityOverTime')}</h3>
          {quantityTimeline.length === 0 ? (
            <ChartPlaceholder />
          ) : (
          <div className="mt-2">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={quantityTimeline} margin={{ left: 8, right: 8, top: 8, bottom: 8 }}>
                <XAxis 
                  dataKey="date" 
                  tick={{ fill: 'currentColor' }}
                  className="text-text dark:text-text-dark"
                  tickFormatter={formatXAxisLabel}
                />
                <YAxis 
                  tick={{ fill: 'currentColor' }}
                  className="text-text dark:text-text-dark"
                />
                <Tooltip 
                  formatter={(v) => [v, t('revenueHistory.quantity')]}
                  labelFormatter={(label) => {
                    const date = new Date(label as string);
                    if (isNaN(date.getTime())) return label;
                    return `${t('revenueHistory.date')}: ${date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}`;
                  }}
                  contentStyle={{
                    backgroundColor: darkMode ? '#0E2B42' : '#FFFFFF',
                    border: `1px solid ${darkMode ? '#1E3A4E' : '#E5E7EB'}`,
                    borderRadius: '0.375rem',
                  }}
                  labelStyle={{ color: darkMode ? '#F9FAFB' : '#000012' }}
                />
                <Bar 
                  dataKey="quantity" 
                  fill="#10b981"
                  name={t('revenueHistory.quantity')}
                  shape={(props: any) => {
                    const dataPoint = quantityTimeline[props.index];
                    return <RoundedBar {...props} fill={dataPoint?.isSelected ? '#10b981' : '#6b7280'} />;
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          )}
        </div>

        {/* Revenue Over Time Chart */}
        <div className="bg-card dark:bg-card-dark rounded-lg shadow-md p-4">
          <h3 className="font-semibold mb-2 text-text dark:text-text-dark">{t('revenueHistory.revenueOverTime')}</h3>
          {revenueTimeline.length === 0 ? (
            <ChartPlaceholder />
          ) : (
          <div className="mt-2">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={revenueTimeline} margin={{ left: 8, right: 8, top: 8, bottom: 8 }}>
                <XAxis 
                  dataKey="date" 
                  tick={{ fill: 'currentColor' }}
                  className="text-text dark:text-text-dark"
                  tickFormatter={formatXAxisLabel}
                />
                <YAxis 
                  tickFormatter={(v) => eur(v).replace("€", "")} 
                  tick={{ fill: 'currentColor' }}
                  className="text-text dark:text-text-dark"
                />
                <Tooltip 
                  formatter={(v) => [eur(v as number), t('revenueHistory.revenue')]}
                  labelFormatter={(label) => {
                    const date = new Date(label as string);
                    if (isNaN(date.getTime())) return label;
                    return `${t('revenueHistory.date')}: ${date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}`;
                  }}
                  contentStyle={{
                    backgroundColor: darkMode ? '#0E2B42' : '#FFFFFF',
                    border: `1px solid ${darkMode ? '#1E3A4E' : '#E5E7EB'}`,
                    borderRadius: '0.375rem',
                  }}
                  labelStyle={{ color: darkMode ? '#F9FAFB' : '#000012' }}
                />
                {/* Base continuous line in contextual color */}
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#6b7280" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  name=""
                  dot={false}
                  legendType="none"
                />
                {/* Overlay line for selected period - only shows green line where isSelected is true */}
                <Line 
                  type="monotone" 
                  dataKey={(entry: any) => entry.isSelected ? entry.revenue : null}
                  stroke="#10b981" 
                  strokeWidth={3}
                  name={t('revenueHistory.revenue')}
                  dot={(props: any) => {
                    const { cx, cy, index } = props;
                    if (cx == null || cy == null) return <></>;
                    const dataPoint = revenueTimeline[index];
                    if (!dataPoint?.isSelected) return <></>;
                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={5}
                        fill="#10b981"
                        strokeWidth={2}
                        stroke="#10b981"
                      />
                    );
                  }}
                  connectNulls={true}
                />
                {/* Contextual dots */}
                <Line 
                  type="monotone" 
                  dataKey={(entry: any) => !entry.isSelected ? entry.revenue : null}
                  stroke="transparent"
                  strokeWidth={0}
                  name=""
                  legendType="none"
                  dot={(props: any) => {
                    const { cx, cy, index } = props;
                    if (cx == null || cy == null) return <></>;
                    const dataPoint = revenueTimeline[index];
                    if (dataPoint?.isSelected) return <></>;
                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={3}
                        fill="#6b7280"
                        strokeWidth={1}
                        stroke="#6b7280"
                      />
                    );
                  }}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          )}
        </div>
      </div>
      
      <AppFooter />
    </div>
  );
};

export default RevenueHistoryItemDetailPage;